import { useMutation, useQuery } from '@apollo/client';
import { FileDropzoneInput, useArchiveFile, useUploadFiles } from '@newfront-insurance/admin-ui/files';
import { SegmentedControlV2 } from '@newfront-insurance/core-ui';
import { Button, ComboboxInput, Input, Textarea } from '@newfront-insurance/core-ui/v2';
import { useAnalytics } from '@newfront-insurance/data-layer-client';
import { useNotify } from '@newfront-insurance/next-notifications';
import { FileArchive, Loader2, Plus, Trash2 } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { normalizeSkillCommand, suggestSkillCommand } from '../../../lib/skill-command-utils';
import { GET_CUSTOM_SKILLS } from '../../../queries/chats';
import type { CustomSkillRecord, NovaSkill } from '../../../queries/chats';
import { BUILD_CUSTOM_SKILL, CREATE_CUSTOM_SKILL } from '../../../queries/mutations';
import { Suggestion, Suggestions } from '../../ai-elements';
import { useSkillsSafe } from '../contexts/skills-context';
import { getLengthBucket, NOVA_ANALYTICS_EVENTS, trackNovaEvent } from '../lib/analytics';

export const ZIP_ACCEPT = '.zip,application/zip,application/x-zip-compressed';
const ALL_CUSTOM_SKILL_FILTERS = { enabled: [true, false] };
const fieldLabelClassName = "font-['Open_Sans',sans-serif] text-[14px] leading-5 font-semibold text-[#262D46]";
const fieldCardClassName = 'rounded-[4px] border border-[#BEC7D0] bg-white';
const formFieldWrapperClassName = 'flex flex-col gap-2';
const inputClassName = "font-['Open_Sans',sans-serif] text-[14px] leading-5 text-[#262D46] placeholder:text-[#98A6B5]";
const textareaClassName =
  "font-['Open_Sans',sans-serif] text-[14px] leading-5 text-[#262D46] placeholder:text-[#98A6B5] min-h-[210px] resize-none rounded-[4px]";
const createViewContentClassName = 'space-y-[25px] px-5 py-5';
const primaryFooterButtonClassName = "min-w-[112px] font-['Open_Sans',sans-serif] text-[14px] leading-5";
const secondaryFooterButtonClassName = "min-w-[112px] font-['Open_Sans',sans-serif] text-[14px] leading-5";
const titleClassName = "font-['Open_Sans',sans-serif] text-[17px] leading-6 font-normal text-[#262D46]";
const helperTextClassName = "font-['Open_Sans',sans-serif] text-[12px] leading-4 text-[#546A83]";
const errorTextClassName = "font-['Open_Sans',sans-serif] text-[12px] leading-4 text-[#C5342A]";
const slashPrefixClassName =
  "flex h-[38px] w-10 shrink-0 items-center justify-center border-r border-[#BEC7D0] bg-[#FAFBFC] font-['Open_Sans',sans-serif] text-[14px] leading-5 text-[#98A6B5]";
const slashCommandFieldClassName = 'flex overflow-hidden rounded-[4px] border border-[#BEC7D0] bg-white';
const segmentedControlOptionClassName =
  "!min-w-[100px] !rounded-full !border-transparent !px-8 !py-1 !font-['Open_Sans',sans-serif] " +
  '!text-[12px] !leading-4 !font-normal [&.isActive]:!border-transparent [&.isActive]:!bg-[#F4F5F6] ' +
  '[&.isActive]:!shadow-none';
const createSkillCommandRegex = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
const customSkillDropzoneClassName =
  'flex min-h-[154px] items-center justify-center rounded-[4px] border border-dashed border-[#BEC7D0] bg-white px-6 text-center transition-colors hover:border-[#257FFB]';

export type CreateSkillMode = 'create' | 'import';

export interface UploadedCustomSkillBundle {
  fileUuid: string;
  name: string;
}

export interface CreateSkillDraft {
  command: string;
  description: string;
  category: string;
  instructions: string;
}

type CreateSkillField = 'command' | 'description' | 'instructions';

interface CreateSkillValidationErrors {
  command?: string;
  description?: string;
  instructions?: string;
}

export const EMPTY_CREATE_SKILL_DRAFT: CreateSkillDraft = {
  command: '',
  description: '',
  category: '',
  instructions: '',
};

export function getErrorMessage(error: unknown, fallback: string) {
  return error instanceof Error && error.message ? error.message : fallback;
}

export function getCategoryLabel(skill: CustomSkillRecord) {
  return skill.frontmatter.category?.trim() || 'Uncategorized';
}

export function isZipFile(file: File) {
  return file.name.toLowerCase().endsWith('.zip');
}

function collectCategories(values: Array<string | null | undefined>) {
  return [...new Set(values.map((value) => value?.trim()).filter(Boolean) as string[])].sort((left, right) =>
    left.localeCompare(right),
  );
}

export function buildSuggestedSkillCategories(
  runtimeSkills: Array<Pick<NovaSkill, 'category'>> = [],
  customSkills: Array<Pick<CustomSkillRecord, 'frontmatter'>> = [],
) {
  return collectCategories([
    ...runtimeSkills.map((skill) => skill.category),
    ...customSkills.map((skill) => skill.frontmatter.category),
  ]);
}

export function isCreateSkillDraftValid(draft: CreateSkillDraft) {
  return Boolean(draft.command.trim() && draft.description.trim() && draft.instructions.trim());
}

export function validateCreateSkillDraft(
  draft: CreateSkillDraft,
  existingCommands: string[] = [],
): CreateSkillValidationErrors {
  const errors: CreateSkillValidationErrors = {};
  const rawCommand = draft.command.trim();
  const suggestedCommand = suggestSkillCommand(rawCommand);

  if (!rawCommand) {
    errors.command = 'Enter a slash command name.';
  } else if (existingCommands.some((command) => normalizeSkillCommand(command) === suggestedCommand)) {
    errors.command = 'That slash command already exists.';
  } else if (rawCommand !== suggestedCommand || !createSkillCommandRegex.test(suggestedCommand)) {
    errors.command = 'Use lowercase letters, numbers, and single hyphens only.';
  }

  if (!draft.description.trim()) {
    errors.description = 'Enter a short description.';
  }

  if (!draft.instructions.trim()) {
    errors.instructions = 'Enter the skill content.';
  }

  return errors;
}

export function useCustomSkillBundleUpload() {
  const notify = useNotify();
  const { upload } = useUploadFiles();
  const { mutateAsync: archiveFile } = useArchiveFile({});

  const archiveUploadedBundle = useCallback(
    async (bundle: UploadedCustomSkillBundle | null) => {
      if (!bundle?.fileUuid) {
        return;
      }

      try {
        await archiveFile({ fileUuid: bundle.fileUuid });
      } catch {
        // Best-effort cleanup for abandoned uploaded bundles.
      }
    },
    [archiveFile],
  );

  const uploadBundle = useCallback(
    async (file: File, uploadFile: { rawFile: File; category?: string }) => {
      if (!isZipFile(file)) {
        throw new Error('Upload a .zip bundle to create a custom skill.');
      }

      const uploadedFiles = await upload({
        files: [
          {
            ...uploadFile,
            category: 'AI_CHAT_ATTACHMENT',
          },
        ],
      });

      const uploadedFileUuid = uploadedFiles[0]?.id;
      if (!uploadedFileUuid) {
        throw new Error('Upload succeeded without a file UUID.');
      }

      return {
        fileUuid: uploadedFileUuid,
        name: file.name,
      } satisfies UploadedCustomSkillBundle;
    },
    [upload],
  );

  return {
    uploadBundle,
    archiveUploadedBundle,
    notifyUploadError: (error: unknown, fallback: string) => {
      notify.error(getErrorMessage(error, fallback));
    },
  };
}

export function CreateSkillView({
  onBack,
  initialMode = 'create',
}: {
  onBack: () => void;
  initialMode?: CreateSkillMode;
}) {
  const notify = useNotify();
  const analytics = useAnalytics();
  const skillsContext = useSkillsSafe();
  const { uploadBundle, archiveUploadedBundle, notifyUploadError } = useCustomSkillBundleUpload();
  const [buildCustomSkill] = useMutation(BUILD_CUSTOM_SKILL);
  const [createCustomSkill] = useMutation(CREATE_CUSTOM_SKILL);
  const instructionsRef = useRef<HTMLTextAreaElement>(null);
  const importedBundleRef = useRef<UploadedCustomSkillBundle | null>(null);
  const [mode, setMode] = useState<CreateSkillMode>(initialMode);
  const [draft, setDraft] = useState(EMPTY_CREATE_SKILL_DRAFT);
  const [importedBundle, setImportedBundle] = useState<UploadedCustomSkillBundle | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [isUploadingImportFile, setIsUploadingImportFile] = useState(false);
  const [touchedFields, setTouchedFields] = useState<Record<CreateSkillField, boolean>>({
    command: false,
    description: false,
    instructions: false,
  });
  const [showCreateValidation, setShowCreateValidation] = useState(false);
  const { data: customSkillsData, refetch: refetchCustomSkills } = useQuery(GET_CUSTOM_SKILLS, {
    variables: { filters: ALL_CUSTOM_SKILL_FILTERS },
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  });

  const categorySuggestions = useMemo(
    () => buildSuggestedSkillCategories(skillsContext?.skills ?? [], customSkillsData?.customSkills ?? []),
    [customSkillsData?.customSkills, skillsContext?.skills],
  );
  const existingSkillCommands = useMemo(
    () => [
      ...(skillsContext?.skills ?? []).map((skill) => skill.command),
      ...(customSkillsData?.customSkills ?? []).map((skill) => skill.command),
    ],
    [customSkillsData?.customSkills, skillsContext?.skills],
  );
  const createValidationErrors = useMemo(
    () => validateCreateSkillDraft(draft, existingSkillCommands),
    [draft, existingSkillCommands],
  );
  const suggestedCommand = useMemo(() => suggestSkillCommand(draft.command), [draft.command]);
  const shouldShowCommandSuggestion = Boolean(
    draft.command.trim() &&
      suggestedCommand &&
      draft.command.trim() !== suggestedCommand &&
      (showCreateValidation || touchedFields.command) &&
      createValidationErrors.command,
  );
  const hasCreateValidationErrors = Object.keys(createValidationErrors).length > 0;
  const descriptionCount = draft.description.length;
  const isCreateDisabled =
    mode === 'import'
      ? !importedBundle?.fileUuid || isCreating || isUploadingImportFile
      : hasCreateValidationErrors || isCreating;

  useEffect(() => {
    importedBundleRef.current = importedBundle;
  }, [importedBundle]);

  const clearImportedBundle = useCallback(async () => {
    if (!importedBundleRef.current) {
      return;
    }

    const currentBundle = importedBundleRef.current;
    importedBundleRef.current = null;
    setImportedBundle(null);
    await archiveUploadedBundle(currentBundle);
  }, [archiveUploadedBundle]);

  const handleBack = useCallback(async () => {
    await clearImportedBundle();
    onBack();
  }, [clearImportedBundle, onBack]);

  const handleImportDrop = useCallback(
    async (files: Array<{ rawFile: File; category?: string }>) => {
      const nextFile = files[0]?.rawFile;

      if (!nextFile) {
        return;
      }

      setIsUploadingImportFile(true);

      try {
        if (importedBundleRef.current) {
          await archiveUploadedBundle(importedBundleRef.current);
        }

        const nextBundle = await uploadBundle(nextFile, files[0]);
        importedBundleRef.current = nextBundle;
        setImportedBundle(nextBundle);
      } catch (uploadError) {
        notifyUploadError(uploadError, 'Unable to upload custom skill ZIP.');
      } finally {
        setIsUploadingImportFile(false);
      }
    },
    [archiveUploadedBundle, notifyUploadError, uploadBundle],
  );

  const handleCreate = useCallback(async () => {
    if (mode === 'create' && hasCreateValidationErrors) {
      setShowCreateValidation(true);
      trackNovaEvent(analytics, NOVA_ANALYTICS_EVENTS.SKILL_CREATION_VALIDATION_FAILED, {
        commandHandle: suggestedCommand,
        creationMode: mode,
        errorFields: Object.keys(createValidationErrors),
      });
      return;
    }

    setIsCreating(true);
    trackNovaEvent(analytics, NOVA_ANALYTICS_EVENTS.SKILL_CREATION_STARTED, {
      category: mode === 'create' ? draft.category.trim() || undefined : undefined,
      commandHandle: mode === 'create' ? suggestedCommand : undefined,
      creationMode: mode,
      descriptionLengthBucket: mode === 'create' ? getLengthBucket(draft.description.trim().length) : undefined,
      hasCategory: mode === 'create' ? Boolean(draft.category.trim()) : undefined,
      instructionLengthBucket: mode === 'create' ? getLengthBucket(draft.instructions.trim().length) : undefined,
      skillSource: mode === 'import' ? 'zip' : 'manual',
    });
    try {
      let createdSkill: CustomSkillRecord | undefined;

      if (mode === 'import') {
        if (!importedBundle?.fileUuid) {
          notify.error('Choose a .zip bundle to create a custom skill.');
          return;
        }

        const { data } = await createCustomSkill({
          variables: {
            command: {
              fileUuid: importedBundle.fileUuid,
              enabled: true,
            },
          },
        });
        createdSkill = data?.createCustomSkill;
      } else {
        const { data } = await buildCustomSkill({
          variables: {
            command: {
              frontmatter: {
                name: suggestSkillCommand(draft.command),
                description: draft.description.trim(),
                category: draft.category.trim() || null,
              },
              content: draft.instructions.trim(),
              enabled: true,
            },
          },
        });

        if (!data?.buildCustomSkill) {
          throw new Error('Missing created custom skill.');
        }
        createdSkill = data.buildCustomSkill;
      }

      await Promise.all([skillsContext?.refetchSkills(), refetchCustomSkills({ filters: ALL_CUSTOM_SKILL_FILTERS })]);
      importedBundleRef.current = null;
      setImportedBundle(null);
      trackNovaEvent(analytics, NOVA_ANALYTICS_EVENTS.SKILL_CREATION_SUCCEEDED, {
        category: createdSkill?.frontmatter.category,
        commandHandle: createdSkill?.command,
        creationMode: mode,
        skillId: createdSkill?.uuid,
        skillSource: createdSkill?.source,
      });
      notify.success('Custom skill created.');
      onBack();
    } catch (createError) {
      trackNovaEvent(analytics, NOVA_ANALYTICS_EVENTS.SKILL_CREATION_FAILED, {
        commandHandle: mode === 'create' ? suggestedCommand : undefined,
        creationMode: mode,
        errorName: createError instanceof Error ? createError.name : undefined,
        skillSource: mode === 'import' ? 'zip' : 'manual',
      });
      notify.error(getErrorMessage(createError, 'Unable to create custom skill.'));
    } finally {
      setIsCreating(false);
    }
  }, [
    buildCustomSkill,
    analytics,
    createCustomSkill,
    createValidationErrors,
    draft,
    hasCreateValidationErrors,
    importedBundle,
    mode,
    notify,
    onBack,
    refetchCustomSkills,
    skillsContext,
    suggestedCommand,
  ]);

  useEffect(() => {
    if (!instructionsRef.current) {
      return;
    }

    instructionsRef.current.style.height = 'auto';
    instructionsRef.current.style.height = `${Math.max(instructionsRef.current.scrollHeight + 2, 210)}px`;
  }, [draft.instructions]);

  useEffect(() => {
    return () => {
      if (importedBundleRef.current) {
        void archiveUploadedBundle(importedBundleRef.current);
        importedBundleRef.current = null;
      }
    };
  }, [archiveUploadedBundle]);

  return (
    <div className="flex min-h-0 flex-1 flex-col bg-white">
      <div className="min-h-0 flex-1 overflow-y-auto">
        <div className={createViewContentClassName}>
          <h2 className={titleClassName}>Add new skill</h2>

          <SegmentedControlV2
            value={mode}
            onChange={(value) => {
              const nextMode = value as CreateSkillMode;
              if (mode === 'import' && nextMode !== 'import' && importedBundleRef.current) {
                void clearImportedBundle();
              }
              setMode(nextMode);
            }}
            options={[
              { label: 'Create new', value: 'create' },
              { label: 'Import file', value: 'import' },
            ]}
            activeColor="default"
            inactiveColor="#546A83"
            fontWeight={400}
            inactiveFontWeight={400}
            containerClassname="!inline-flex !rounded-full !border-[#DEE2E7] !bg-white !p-1"
            optionClassname={segmentedControlOptionClassName}
          />

          {mode === 'import' ? (
            <div className="space-y-4">
              <FileDropzoneInput
                multiple={false}
                accept={ZIP_ACCEPT}
                category="AI_CHAT_ATTACHMENT"
                loading={isUploadingImportFile}
                onDrop={(files) => void handleImportDrop(files)}
                customDropzoneComponent={
                  <div className={customSkillDropzoneClassName}>
                    {isUploadingImportFile ? (
                      <div className="inline-flex items-center gap-3 text-[14px] leading-5 text-[#546A83]">
                        <Loader2 className="h-5 w-5 animate-spin text-[#257FFB]" />
                        <span>Uploading skill file...</span>
                      </div>
                    ) : (
                      <div className="inline-flex items-center gap-4 text-[14px] leading-5 text-[#257FFB]">
                        <Plus className="h-6 w-6 shrink-0" strokeWidth={2.5} />
                        <span>Drop a skill file here or choose a file</span>
                      </div>
                    )}
                  </div>
                }
              />

              {importedBundle ? (
                <div className={`${fieldCardClassName} flex items-center justify-between gap-4 px-4 py-4`}>
                  <div className="flex min-w-0 items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-[6px] bg-[#F4F8FE] shadow-[0px_1px_4px_0px_rgba(0,0,0,0.15)]">
                      <FileArchive className="h-4 w-4 text-[#546A83]" />
                    </div>
                    <div className="min-w-0">
                      <p className="truncate text-sm text-[#262D46]">{importedBundle.name}</p>
                      <p className="mt-1 text-xs text-[#546A83]">ZIP bundle uploaded. Ready to import into Nova.</p>
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={() => void clearImportedBundle()}
                    disabled={isCreating || isUploadingImportFile}
                    className="text-[#546A83] transition-colors hover:text-[#262D46] disabled:opacity-50"
                    aria-label="Remove uploaded ZIP bundle"
                  >
                    <Trash2 className="h-5 w-5" />
                  </button>
                </div>
              ) : null}
            </div>
          ) : (
            <div className="space-y-4">
              <div className={formFieldWrapperClassName}>
                <label htmlFor="custom-skill-command" className={fieldLabelClassName}>
                  Slash command name
                </label>
                <div
                  className={`${slashCommandFieldClassName} ${
                    (showCreateValidation || touchedFields.command) && createValidationErrors.command
                      ? 'border-[#C5342A]'
                      : ''
                  }`}
                >
                  <div
                    className={`${slashPrefixClassName} ${
                      (showCreateValidation || touchedFields.command) && createValidationErrors.command
                        ? 'border-[#C5342A]'
                        : ''
                    }`}
                  >
                    /
                  </div>
                  <input
                    id="custom-skill-command"
                    aria-label="Slash command name"
                    type="text"
                    placeholder="e.g. compare-quotes"
                    value={draft.command}
                    onChange={(event) => setDraft((currentDraft) => ({ ...currentDraft, command: event.target.value }))}
                    onBlur={() => setTouchedFields((current) => ({ ...current, command: true }))}
                    disabled={isCreating}
                    className={`${inputClassName} h-[38px] w-full border-0 bg-transparent px-6 outline-none disabled:cursor-not-allowed disabled:opacity-50`}
                  />
                </div>
                {shouldShowCommandSuggestion ? (
                  <Suggestions>
                    <Suggestion
                      suggestion={suggestedCommand}
                      className="h-auto border border-[#D9E6FF] bg-[#F4F8FE] px-3 py-1.5 text-[12px] leading-4 font-semibold text-[#257FFB] hover:bg-[#E8F1FE]"
                      onClick={(nextCommand) => {
                        setDraft((currentDraft) => ({ ...currentDraft, command: nextCommand }));
                        setTouchedFields((current) => ({ ...current, command: true }));
                      }}
                    >
                      <span className="inline-flex items-center gap-1">
                        <span>Use</span>
                        <span className="font-mono">/{suggestedCommand}</span>
                      </span>
                    </Suggestion>
                  </Suggestions>
                ) : null}
                {(showCreateValidation || touchedFields.command) && createValidationErrors.command ? (
                  <p className={errorTextClassName}>{createValidationErrors.command}</p>
                ) : null}
              </div>

              <div className={formFieldWrapperClassName}>
                <label htmlFor="custom-skill-description" className={fieldLabelClassName}>
                  Short description*
                </label>
                <Input
                  type="text"
                  fieldId="custom-skill-description"
                  aria-label="Short description"
                  value={draft.description}
                  onChange={(event) =>
                    setDraft((currentDraft) => ({
                      ...currentDraft,
                      description: event.target.value.slice(0, 88),
                    }))
                  }
                  onBlur={() => setTouchedFields((current) => ({ ...current, description: true }))}
                  placeholder="Placeholder text"
                  maxLength={88}
                  disabled={isCreating}
                  wrapperClassName={`bg-white ${
                    (showCreateValidation || touchedFields.description) && createValidationErrors.description
                      ? 'border-[#C5342A]'
                      : 'border-[#BEC7D0]'
                  }`}
                  inputClassName={inputClassName}
                />
                {(showCreateValidation || touchedFields.description) && createValidationErrors.description ? (
                  <p className={errorTextClassName}>{createValidationErrors.description}</p>
                ) : null}
                <p className={helperTextClassName}>{descriptionCount}/88 characters</p>
              </div>

              <div className={formFieldWrapperClassName}>
                <label htmlFor="custom-skill-category" className={fieldLabelClassName}>
                  Category
                </label>
                <ComboboxInput
                  fieldId="custom-skill-category"
                  aria-label="Category"
                  value={draft.category}
                  onChange={(event) => setDraft((currentDraft) => ({ ...currentDraft, category: event.target.value }))}
                  suggestions={categorySuggestions}
                  emptyText="Type to create a new category"
                  maxSuggestions={8}
                  minChars={0}
                  showOnFocus
                  placeholder="e.g. renewals"
                  disabled={isCreating}
                  wrapperClassName="border-[#BEC7D0] bg-white"
                  inputClassName={inputClassName}
                />
                {categorySuggestions.length > 0 ? (
                  <p className={helperTextClassName}>Choose an existing category or type a new one.</p>
                ) : null}
              </div>

              <div className={formFieldWrapperClassName}>
                <label htmlFor="custom-skill-content" className={fieldLabelClassName}>
                  Skill content*
                </label>
                <Textarea
                  ref={instructionsRef}
                  id="custom-skill-content"
                  aria-label="Skill content"
                  value={draft.instructions}
                  onChange={(event) =>
                    setDraft((currentDraft) => ({ ...currentDraft, instructions: event.target.value }))
                  }
                  onBlur={() => setTouchedFields((current) => ({ ...current, instructions: true }))}
                  placeholder="Describe what Nova should do when this skill is used."
                  className={`${textareaClassName} ${
                    (showCreateValidation || touchedFields.instructions) && createValidationErrors.instructions
                      ? 'border-[#C5342A]'
                      : ''
                  }`}
                  disabled={isCreating}
                />
                {(showCreateValidation || touchedFields.instructions) && createValidationErrors.instructions ? (
                  <p className={errorTextClassName}>{createValidationErrors.instructions}</p>
                ) : null}
              </div>
            </div>
          )}
        </div>
      </div>
      <div className="flex items-center justify-end gap-[13px] border-t border-[#DEE2E7] bg-white px-5 py-4">
        <Button
          type="button"
          variant="secondary"
          className={secondaryFooterButtonClassName}
          onClick={() => void handleBack()}
          disabled={isCreating}
        >
          Cancel
        </Button>
        <Button
          type="button"
          className={primaryFooterButtonClassName}
          disabled={isCreateDisabled}
          onClick={() => void handleCreate()}
        >
          {isCreating ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              {mode === 'import' ? 'Uploading...' : 'Creating...'}
            </>
          ) : mode === 'import' ? (
            'Import skill'
          ) : (
            'Add skill'
          )}
        </Button>
      </div>
    </div>
  );
}
